// The shared vision-card machinery (056-vision-capture-method, B2 — the
// screen), extracted from the 054 VisionSurface so every section of the new
// information architecture (triage banner, challenge landscape, guiding
// policy, benefit frame) renders the SAME lifecycle visual language:
// text + icon tags (never colour alone), dashed assumed / solid confirmed,
// `Saving…` for a pending local event, disputed blocked-open with Reconcile
// and Reject as the only exits, one-click Confirm, in-place edit
// (Enter/blur commit, Escape cancels), and the sealed read-only posture.
//
// Everything here routes mutations through onEmitEdit (the binding contract)
// — no fetches, no shared state. The only local state is view-state.

import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle,
  CircleDashed,
  Loader2,
  Pencil,
  Plus,
  Target,
  Trash2,
} from 'lucide-react';
import type {
  GatedFact,
  SurfaceEdit,
  SurfaceFocus,
} from '../../facilitation/surfaceBinding';

// onEmitEdit per the binding contract returns void; the shell MAY hand back a
// promise resolving `false` on a server refusal so view-state (composer /
// edit drafts) can restore itself — a plain void/undefined return (the
// contract minimum) simply skips the restore.
export type EmitResult = void | boolean | Promise<boolean | void>;

export function textOf(fact: GatedFact): string {
  if (typeof fact.content.text === 'string') return fact.content.text;
  if (typeof fact.content.statement === 'string') return fact.content.statement;
  if (typeof fact.content.name === 'string') {
    const role = typeof fact.content.role === 'string' ? fact.content.role : '';
    return role ? `${fact.content.name}, ${role}` : fact.content.name;
  }
  return '';
}

// Restore-on-refusal helper: when the emit handler returns a promise that
// resolves `false` (the shell's server-reject signal), run the revert.
export function onRefusal(result: EmitResult, revert: () => void): void {
  if (result && typeof (result as Promise<unknown>).then === 'function') {
    (result as Promise<boolean | void>)
      .then((ok) => {
        if (ok === false) revert();
      })
      .catch(() => revert());
  }
}

// ── §4 lifecycle tags — literal text + icon, never colour alone ─────────────

export const LifecycleTag: React.FC<{ fact: GatedFact }> = ({ fact }) => {
  if (fact.provisionalLocal) {
    return (
      <span className="vsf-tag vsf-tag--saving">
        <Loader2 size={12} className="vsf-spin" aria-hidden="true" /> Saving…
      </span>
    );
  }
  switch (fact.lifecycle) {
    case 'confirmed':
      return (
        <span className="vsf-tag vsf-tag--confirmed">
          <CheckCircle size={12} aria-hidden="true" /> Confirmed
        </span>
      );
    case 'disputed':
      return (
        <span className="vsf-tag vsf-tag--disputed">
          <AlertTriangle size={12} aria-hidden="true" /> Disputed
        </span>
      );
    default:
      return (
        <span className="vsf-tag vsf-tag--assumed">
          <CircleDashed size={12} aria-hidden="true" /> Assumed
        </span>
      );
  }
};

// ── The in-place text field: Enter/blur commit, Escape cancels ──────────────

export const InPlaceField: React.FC<{
  fact: GatedFact;
  noun: string;
  disabled: boolean;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}> = ({ fact, noun, disabled, onEmitEdit, onPublishFocus }) => {
  const committed = textOf(fact);
  const [draft, setDraft] = useState(committed);
  const committedRef = useRef(committed);

  // The server-ordered value is the source of truth: when it changes (ack of
  // an edit, another editor's event), the field re-syncs.
  useEffect(() => {
    setDraft(committed);
    committedRef.current = committed;
  }, [committed]);

  const commit = () => {
    const prior = committedRef.current;
    if (draft === prior) return; // nothing changed — no event
    committedRef.current = draft; // a following blur must not re-commit
    const result = onEmitEdit({
      capabilityId: 'vision.edit_fact',
      input: { factId: fact.factId, text: draft },
    });
    // Server reject: the text reverts to the prior value (the shell's alert
    // explains).
    onRefusal(result, () => {
      committedRef.current = prior;
      setDraft(prior);
    });
  };

  return (
    <input
      className="vsf-field"
      aria-label={`Edit ${noun}`}
      value={draft}
      disabled={disabled}
      // The new card's field takes focus after a direct add / raise (the
      // provisional card is the freshly-mounted one).
      autoFocus={fact.provisionalLocal === true}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => onPublishFocus?.({ factId: fact.factId })}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') setDraft(committedRef.current);
      }}
    />
  );
};

// ── The disputed blocked-open block + reconcile (054 pattern, held open) ────

type DisputeStatement = { text?: string; attribution?: { statedByLabel?: string } | null };

function disputeStatementsOf(fact: GatedFact): DisputeStatement[] {
  const dispute = fact.content.dispute as { statements?: DisputeStatement[] } | undefined;
  return Array.isArray(dispute?.statements) ? dispute.statements : [];
}

export const DisputedBlock: React.FC<{
  fact: GatedFact;
  noun: string;
  disabled: boolean;
  /** Sealed: both statements + attributions stay readable, Reconcile is gone. */
  readOnly?: boolean;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}> = ({ fact, noun, disabled, readOnly, onEmitEdit }) => {
  const statements = disputeStatementsOf(fact);
  const [reconciling, setReconciling] = useState(false);
  const [draft, setDraft] = useState('');

  if (reconciling && !readOnly) {
    // One editable field, prefilled with the FIRST statement; Enter commits
    // the agreed wording, Escape cancels back to blocked-open.
    return (
      <div className="vsf-reconcile">
        <input
          className="vsf-field"
          aria-label="Agreed wording"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setReconciling(false);
              onEmitEdit({
                capabilityId: 'vision.reconcile_fact',
                input: { factId: fact.factId, text: draft },
              });
            } else if (e.key === 'Escape') {
              setReconciling(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="vsf-dispute">
      <div className="vsf-dispute-head">Two accounts of this {noun}</div>
      {statements.map((statement, i) => (
        <div key={i} className="vsf-dispute-row">
          <span className="vsf-dispute-text">“{statement.text ?? ''}”</span>
          <span className="vsf-dispute-attr">
            · {statement.attribution?.statedByLabel || 'unattributed'}
          </span>
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          className="vsf-btn"
          disabled={disabled}
          onClick={() => {
            setDraft(statements[0]?.text ?? '');
            setReconciling(true);
          }}
        >
          Reconcile: agree one wording
        </button>
      )}
    </div>
  );
};

export const DisputeComposer: React.FC<{
  fact: GatedFact;
  noun: string;
  disabled: boolean;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}> = ({ fact, noun, disabled, onEmitEdit }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [speaker, setSpeaker] = useState('');
  if (!open) {
    return (
      <button type="button" className="vsf-btn" disabled={disabled} onClick={() => setOpen(true)}>
        Dispute
      </button>
    );
  }
  const save = () => {
    if (!text.trim() || !speaker.trim()) return;
    onEmitEdit({
      capabilityId: 'vision.dispute_fact',
      input: {
        factId: fact.factId,
        text: text.trim(),
        attribution: { statedByLabel: speaker.trim() },
      },
    });
    setOpen(false);
    setText('');
    setSpeaker('');
  };
  return (
    <div className="vsf-dispute-compose" role="group" aria-label={`Dispute ${noun}`}>
      <input
        className="vsf-field"
        aria-label={`Different account of ${noun}`}
        placeholder="What is the different account?"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <input
        className="vsf-field"
        aria-label="Who gave this account?"
        placeholder="Who said this?"
        value={speaker}
        onChange={(event) => setSpeaker(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') save(); }}
      />
      <button type="button" className="vsf-btn" onClick={() => setOpen(false)}>Cancel</button>
      <button type="button" className="vsf-btn" disabled={!text.trim() || !speaker.trim()} onClick={save}>
        Save dispute
      </button>
    </div>
  );
};

// ── The generic fact card — every section's cards share this grammar ────────

export interface FactCardProps {
  fact: GatedFact;
  /** The card's noun for accessible names (challenge, driver, objective…). */
  noun: string;
  sealed: boolean;
  frozen: boolean;
  pulsing: boolean;
  /** Present on landscape rows: the crux toggle (aria-pressed on EVERY row;
   * the coral accent stays reserved for the one designated row). */
  cruxToggle?: { designated: boolean; input: Record<string, unknown> };
  /** A non-null reason renders Confirm disabled with the what-is-missing
   * tooltip (the ownerless benefit — the server refuses anyway). */
  confirmBlockedReason?: string | null;
  /** Rendered between the text and the foot (evidence chips, links, pips). */
  detail?: React.ReactNode;
  /** Rendered after the foot (the drill-in — second and last disclosure). */
  drillIn?: React.ReactNode;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}

export const FactCard: React.FC<FactCardProps> = ({
  fact,
  noun,
  sealed,
  frozen,
  pulsing,
  cruxToggle,
  confirmBlockedReason = null,
  detail,
  drillIn,
  registerRef,
  onEmitEdit,
  onPublishFocus,
}) => {
  const busy = fact.provisionalLocal === true;
  const disputed = fact.lifecycle === 'disputed';
  const designated = cruxToggle?.designated === true;

  const className = [
    'vsf-card',
    `vsf-card--${fact.lifecycle}`,
    designated ? 'vsf-card--crux' : '',
    pulsing ? 'vsf-pulse' : '',
    sealed ? 'vsf-card--sealed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // The sealed session: the card renders as STATIC TEXT — no in-place input,
  // no buttons; the lifecycle tag persists as text; a designated crux row
  // keeps a static (non-interactive) marker; detail + drill-in stay readable.
  if (sealed) {
    return (
      <div
        className={className}
        data-fact-id={fact.factId}
        tabIndex={-1}
        ref={(el) => registerRef(fact.factId, el)}
      >
        {disputed ? (
          <DisputedBlock fact={fact} noun={noun} disabled readOnly onEmitEdit={onEmitEdit} />
        ) : (
          <span className="vsf-static-text">{textOf(fact)}</span>
        )}

        {detail}

        <div className="vsf-card-foot">
          <LifecycleTag fact={fact} />
          {designated && (
            <span className="vsf-crux vsf-crux--on vsf-crux--static">
              <Target size={12} aria-hidden="true" /> The crux
            </span>
          )}
        </div>

        {drillIn}
      </div>
    );
  }

  return (
    <div
      className={className}
      data-fact-id={fact.factId}
      tabIndex={-1}
      ref={(el) => registerRef(fact.factId, el)}
    >
      {disputed ? (
        <DisputedBlock
          fact={fact}
          noun={noun}
          disabled={busy || frozen}
          onEmitEdit={onEmitEdit}
        />
      ) : (
        <InPlaceField
          fact={fact}
          noun={noun}
          disabled={frozen}
          onEmitEdit={onEmitEdit}
          onPublishFocus={onPublishFocus}
        />
      )}

      {detail}

      <div className="vsf-card-foot">
        <LifecycleTag fact={fact} />

        {!disputed && fact.lifecycle !== 'confirmed' && (
          // One click, no dialog. Rendered disabled while pending, and while
          // a structural block stands (the tooltip says exactly what's
          // missing — incompleteness is visible, never a surprise 400).
          <button
            type="button"
            className="vsf-btn vsf-confirm"
            disabled={busy || frozen || Boolean(confirmBlockedReason)}
            title={confirmBlockedReason ?? undefined}
            onClick={() =>
              onEmitEdit({
                capabilityId: 'vision.confirm_fact',
                input: { factId: fact.factId },
              })
            }
          >
            <Check size={12} aria-hidden="true" /> Confirm
          </button>
        )}

        {!disputed && fact.lifecycle === 'confirmed' && !busy && (
          // The demotion is labelled so it is never a surprise (054 FR-018).
          <span className="vsf-edit-hint">
            <Pencil size={11} aria-hidden="true" /> Edit to revise
          </span>
        )}

        {!disputed && (
          <DisputeComposer
            fact={fact}
            noun={noun}
            disabled={busy || frozen}
            onEmitEdit={onEmitEdit}
          />
        )}

        {cruxToggle && (
          // The crux toggle is on EVERY landscape row (054 §2.2/§5 kept).
          <button
            type="button"
            className={designated ? 'vsf-crux vsf-crux--on' : 'vsf-crux'}
            aria-pressed={designated}
            aria-label={designated ? `The crux — ${textOf(fact)}` : 'Mark as crux'}
            disabled={busy || frozen}
            onClick={() =>
              onEmitEdit({
                capabilityId: 'vision.designate_crux',
                input: cruxToggle.input,
              })
            }
          >
            <Target size={12} aria-hidden="true" /> {designated ? 'The crux' : 'Mark as crux'}
          </button>
        )}

        <button
          type="button"
          className="vsf-reject"
          aria-label={`Reject ${noun}`}
          disabled={busy || frozen}
          onClick={() =>
            onEmitEdit({
              capabilityId: 'vision.reject_fact',
              input: { factId: fact.factId },
            })
          }
        >
          <Trash2 size={13} aria-hidden="true" />
        </button>
      </div>

      {drillIn}
    </div>
  );
};

// ── The add composers — live from the first render ──────────────────────────

export const Composer: React.FC<{
  label: string;
  placeholder?: string;
  disabled?: boolean;
  onCommit: (text: string) => EmitResult;
}> = ({ label, placeholder, disabled, onCommit }) => {
  const [value, setValue] = useState('');

  const commit = () => {
    if (!value) return;
    const submitted = value;
    setValue(''); // clears in the same keystroke
    // Server reject: the typed text is restored into the composer, not lost.
    onRefusal(onCommit(submitted), () => setValue(submitted));
  };

  return (
    <div className="vsf-composer">
      <Plus size={13} aria-hidden="true" />
      <input
        className="vsf-composer-input"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
    </div>
  );
};
